import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import type { FormatOptionLabelMeta } from "react-select";
import Select from "react-select";
import { type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { Callout, CalloutIntent, LoadingSpinner } from "widgets";

import { BrowserTab } from "@/app-utils/browser-tab.ts";
import { handleTabInForm, type ISelectOption } from "@/app-utils/form.ts";
import { pushRoute } from "@/app-utils/route.ts";
import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import {
  type Group,
  type GroupList,
  type InputSetComparisonGroupInput,
  type InputSetComparisonInput,
  type SetComparison,
  type VariantAnalysisGroup,
} from "@/graphql.ts";
import {
  createGroupFromSetComparison,
  createWarning,
  loadGroups,
  loadVariantComparisonData,
  performSetComparison,
  resetComparisonState,
} from "@/redux/modules/comparison.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE, FEATURE_DISPLAY_NAME, MAX_GROUP_SIZE } from "@/settings/settings.ts";

import { TooltipController } from "./overlays/components/OverlayControllers.js";
import { Dialog } from "./overlays/Dialogs.js";
import ReactSelectField from "./redux-form/ReactSelectField.js";
import TwoSetVennDiagram from "./TwoSetVennDiagram.js";

interface IVennDiagramDialogOwnProps {
  type: string;
  onApply: (groupId: number) => void;
  onCancel: () => void;
  explicitSet?: number[];
  explicitSetLabel?: string;
  items?: Group[];
  selectedItems?: number[];
}

interface IVennDiagramDialogAppStateProps {
  isExecutingSetComparison: boolean;
  isLoadingGroups: boolean;
  setComparison: SetComparison;
  groupData: GroupList;
  variantAnalysisGroup: VariantAnalysisGroup;
}

interface IVennDiagramDialogDispatchProps {
  performSetComparison: (payload: InputSetComparisonInput) => Promise<SetComparison>;
  warn: (selectedItems: number) => void;
  resetComparisonState: () => void;
  createGroupFromSetComparison: (payload: InputSetComparisonGroupInput) => Promise<number>;
  push: (url: string) => void;
  loadGroups: (type: string) => void;
  loadVariantData: (type: string, groupId: number, variantId: number) => void; // type is needed to distinguish the variants from the mutations on the middle tier when a simple id is provided
}

export interface IVennDiagramDialogProps
  extends IVennDiagramDialogAppStateProps,
    IVennDiagramDialogDispatchProps,
    IVennDiagramDialogOwnProps,
    InjectedFormProps<any, any> {}

interface IVennDiagramDialogState {
  selectedItemsCount?: number;
  error?: string;
  lhsOptions?: Partial<Group>[];
  rhsOptions?: Partial<Group>[];
  lhsSelected?: Partial<Group>;
  rhsSelected?: Partial<Group>;
  comparison?: SetComparison;
  leftSectionSelected: boolean;
  middleSectionSelected: boolean;
  rightSectionSelected: boolean;
  allItems: Partial<Group>[];
}

interface ISelectOptionWithGroup extends ISelectOption {
  group: Partial<Group>;
}

function mapStateToProps(state: IAppState, _ownProps: IVennDiagramDialogProps): IVennDiagramDialogAppStateProps {
  return {
    isExecutingSetComparison: state.comparison.isLoading,
    setComparison: state.comparison.setComparison,
    groupData: state.comparison.groupData,
    isLoadingGroups: state.comparison.isLoadingGroups,
    variantAnalysisGroup: state.comparison.variantAnalysisGroup,
  };
}

function mapDispatchToProps(dispatch): IVennDiagramDialogDispatchProps {
  return {
    performSetComparison: (payload: InputSetComparisonInput) => dispatch(performSetComparison(payload)),
    resetComparisonState: () => dispatch(resetComparisonState()),
    warn: (selectedItems: number) => dispatch(createWarning(selectedItems)),
    createGroupFromSetComparison: (payload: InputSetComparisonGroupInput) =>
      dispatch(createGroupFromSetComparison(payload)),
    push: (url: string) => pushRoute(url),
    loadGroups: (type: string) => dispatch(loadGroups(type)),
    loadVariantData: (type: string, groupId: number, variantId: number) =>
      dispatch(loadVariantComparisonData(type, groupId, variantId)),
  };
}

class VennDiagramDialog extends Component<IVennDiagramDialogProps, IVennDiagramDialogState> {
  public static FORM_NAME = "VENN_DIAGRAM_DIALOG_DATA";
  public static LEFT_SET = "LHS_SET";
  public static RIGHT_SET = "RHS_SET";

  private static PSEUDO_GROUP_ID = -1;

  state: IVennDiagramDialogState = {
    selectedItemsCount: 0,
    comparison: {
      left: null,
      middle: null,
      right: null,
    },
    rhsSelected: null,
    lhsSelected: null,
    leftSectionSelected: false,
    middleSectionSelected: false,
    rightSectionSelected: false,
    allItems: null,
  };

  filterSelected(items: Partial<Group>[], selectedItems: number[]) {
    if (selectedItems && selectedItems.length) {
      const selected = [];
      const unselected = [];
      items.forEach((x) => {
        const selectedItem = selectedItems.find((y) => y === x.id);
        if (selectedItem !== undefined) {
          selected.push(x);
        } else {
          unselected.push(x);
        }
      });
      return selected.concat(unselected);
    }
    return items;
  }

  init = (props: IVennDiagramDialogProps) => {
    let allItems: Partial<Group>[] = [];
    if (props.explicitSet) {
      const g: Partial<Group> = {
        id: VennDiagramDialog.PSEUDO_GROUP_ID,
        type: this.props.type,
        deletable: false,
        description: "",
        itemCount: props.explicitSet.length,
        name: props.explicitSetLabel,
      };
      allItems.push(g);
    }
    if (props.items && !this.isVariantComparisonDialog()) {
      allItems = allItems.concat(props.items);
    } else if (props.groupData) {
      allItems = allItems.concat(props.groupData.items);
    }
    allItems = this.filterSelected(allItems, props.selectedItems);

    const lhsSelected = props.selectedItems?.length || props.explicitSet ? allItems[0] : null;
    const rhsSelected = props.selectedItems && props.selectedItems.length > 1 ? allItems[1] : null;
    const lhsItems = this.filterItems(allItems, true, rhsSelected);
    const rhsItems = this.filterItems(allItems, false, lhsSelected);
    this.setState({
      lhsSelected: lhsSelected,
      lhsOptions: lhsItems,
      rhsOptions: rhsItems,
      rhsSelected: rhsSelected,
      allItems,
    });
    this.updateVennDiagramCounts(lhsSelected, rhsSelected);

    if (props.selectedItems && props.selectedItems.length > 2) {
      this.props.warn(props.selectedItems.length);
    }
  };

  componentDidMount() {
    if (this.isVariantComparisonDialog()) {
      // for variants, we have to load the analysis group and the variant-specific group list (with compatible groups only) in any case
      // (when coming from a variant group list, or from a variant table)
      this.props.loadVariantData(this.props.type, this.props.selectedItems?.[0], this.props.explicitSet?.[0]);
    } else if (!this.props.items) {
      // proceed with non-variants:
      // if we are not coming from a group list, we have to load the groups
      this.props.loadGroups(this.props.type);
    } else {
      // else we already have the groups and can proceed immediately
      this.init(this.props);
    }
  }

  componentWillUnmount() {
    this.props.resetComparisonState();
  }

  UNSAFE_componentWillReceiveProps(nextProps: IVennDiagramDialogProps, _nextState: IVennDiagramDialogState) {
    if (nextProps.isLoadingGroups || nextProps.isExecutingSetComparison) {
      return;
    }

    if (
      (!this.isVariantComparisonDialog() &&
        !this.state.allItems &&
        !nextProps.items &&
        nextProps.groupData &&
        nextProps.groupData.items) ||
      (this.isVariantComparisonDialog() && !this.state.allItems && nextProps.groupData && nextProps.groupData.items)
    ) {
      this.init(nextProps);
    }

    if (nextProps.setComparison && nextProps.setComparison !== this.props.setComparison) {
      this.setState({
        comparison: nextProps.setComparison,
        selectedItemsCount: nextProps.setComparison.middle,
      });
    }
  }

  isVariantComparisonDialog = (): boolean => {
    return this.props.type === ENTITY_TYPE.VARIANT || this.props.type === ENTITY_TYPE.MUTATION;
  };

  handleSubmitData = (): Promise<any> => {
    const input: InputSetComparisonGroupInput = {
      type: this.props.type,
      includeLeft: this.state.leftSectionSelected,
      includeMiddle: this.state.middleSectionSelected,
      includeRight: this.state.rightSectionSelected,
      ...(this.state.lhsSelected.id === VennDiagramDialog.PSEUDO_GROUP_ID
        ? { leftItems: this.props.explicitSet }
        : { leftGroupId: this.state.lhsSelected.id }),
      ...(this.state.rhsSelected.id === VennDiagramDialog.PSEUDO_GROUP_ID
        ? { rightItems: this.props.explicitSet }
        : { rightGroupId: this.state.rhsSelected.id }),
    };
    const browserTab = new BrowserTab(`/inprogress`);
    return this.props.createGroupFromSetComparison(input).then(
      (groupId) => {
        this.props.onApply(groupId);
        browserTab.goTo(`/group/${groupId}`);
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  handleCancel = () => {
    this.setState({ selectedItemsCount: 0 });
    this.props.onCancel();
  };

  filterItems(allItems: Partial<Group>[], _lhs: boolean, excludeGroup: Partial<Group>) {
    if (excludeGroup === null) {
      return allItems;
    }
    return allItems.filter((x) => x.id !== excludeGroup.id);
  }

  clearVennDiagram() {
    this.setState({
      comparison: {
        left: null,
        middle: null,
        right: null,
      },
    });
  }

  handleGroupSelectionChange(lhs: boolean, selectedOption: ISelectOptionWithGroup) {
    const selectedGroup: Partial<Group> = selectedOption.group;
    // for explicitly selected rows of a table, directly receive the lhs options that contain only this group
    if (lhs) {
      // if something was selected on the lhs, update the rhs list
      const filteredItems = this.filterItems(this.state.allItems, false, selectedGroup);
      this.setState({
        lhsSelected: selectedGroup,
        rhsOptions: filteredItems,
      } as IVennDiagramDialogState);
      this.updateVennDiagramCounts(selectedGroup, this.state.rhsSelected);
    } else {
      const filteredItems = this.filterItems(this.state.allItems, true, selectedGroup);
      this.setState({
        rhsSelected: selectedGroup,
        lhsOptions: filteredItems,
      } as IVennDiagramDialogState);
      this.updateVennDiagramCounts(this.state.lhsSelected, selectedGroup);
    }
  }

  updateVennDiagramCounts = (lhsGroup: Partial<Group>, rhsGroup: Partial<Group>) => {
    this.clearVennDiagram();
    if (lhsGroup && rhsGroup) {
      let input: InputSetComparisonInput;
      if (lhsGroup.id === VennDiagramDialog.PSEUDO_GROUP_ID) {
        input = {
          type: this.props.type,
          leftItems: this.props.explicitSet,
          rightGroupId: rhsGroup.id,
        };
      } else if (rhsGroup.id === VennDiagramDialog.PSEUDO_GROUP_ID) {
        input = {
          type: this.props.type,
          leftGroupId: lhsGroup.id,
          rightItems: this.props.explicitSet,
        };
      } else {
        input = {
          type: this.props.type,
          leftGroupId: lhsGroup.id,
          rightGroupId: rhsGroup.id,
        };
      }
      this.props.performSetComparison(input);
    }
  };

  generateContent() {
    const lhsSelectOptions = this.generateOptions(this.state.lhsOptions);
    const lhsSelectedOption = this.state.lhsSelected
      ? lhsSelectOptions.find((x) => this.state.lhsSelected.id === x.group.id)
      : null;
    const rhsSelectOptions = this.generateOptions(this.state.rhsOptions);
    const rhsSelectedOption = this.state.rhsSelected
      ? rhsSelectOptions.find((x) => this.state.rhsSelected.id === x.group.id)
      : null;

    const ret: ReactElement[] = [];

    ret.push(
      <TooltipController
        content={<span id="tooltip-hint-lhs">Select group for comparison</span>}
        overlayOptions={{ isOpen: !this.state.lhsSelected }}
      >
        {(tooltipTriggerProps) => (
          <div className={"left-half"} ref={tooltipTriggerProps.ref}>
            <Select
              key="venn-diagram-lhs-field"
              className="react-select"
              classNamePrefix={"react-select"}
              name={VennDiagramDialog.LEFT_SET}
              placeholder="Please select..."
              options={lhsSelectOptions}
              defaultValue={lhsSelectedOption}
              formatOptionLabel={this.formatOptionLabel}
              isClearable={false}
              isSearchable={true}
              isDisabled={this.isVariantComparisonDialog()}
              onChange={(selectedOption: ISelectOptionWithGroup) =>
                this.handleGroupSelectionChange(true, selectedOption)
              }
            />
          </div>
        )}
      </TooltipController>,
    );
    // className = "right-half"
    ret.push(
      <TooltipController
        content={<span id="tooltip-hint-rhs">Select group for comparison</span>}
        overlayOptions={{ isOpen: this.state.lhsSelected && !this.state.rhsSelected }}
      >
        {(tooltipTriggerProps) => (
          <div className={"right-half"} ref={tooltipTriggerProps.ref}>
            <Select
              key="venn-diagram-rhs-field"
              className="react-select"
              classNamePrefix={"react-select"}
              name={VennDiagramDialog.RIGHT_SET}
              placeholder="Please select..."
              options={rhsSelectOptions}
              defaultValue={rhsSelectedOption}
              component={ReactSelectField}
              formatOptionLabel={this.formatOptionLabel}
              isClearable={false}
              isSearchable={true}
              onChange={(selectedOption: ISelectOptionWithGroup) =>
                this.handleGroupSelectionChange(false, selectedOption)
              }
            />
          </div>
        )}
      </TooltipController>,
    );

    ret.push(
      <div key="two-set-venn-diagram" className="venn-diagram-container">
        <TwoSetVennDiagram
          isLoading={this.props.isExecutingSetComparison}
          leftCount={this.state.comparison.left}
          intersectionCount={this.state.comparison.middle}
          rightCount={this.state.comparison.right}
          onSelectionChange={this.selectionChanged}
        />
      </div>,
    );
    if (this.state.selectedItemsCount < 1) {
      ret.push(
        <Callout intent={CalloutIntent.Info}>
          Please select one or more non-empty sections of the Venn diagram to proceed.
        </Callout>,
      );
    }

    if (this.state.error) {
      ret.push(<Callout intent={CalloutIntent.Danger}>{this.state.error}</Callout>);
    }
    return ret;
  }

  render() {
    const title = capitalizeFirstLetter(FEATURE_DISPLAY_NAME[this.props.type]) + " Group Comparison";
    const content = this.generateContent();
    const warning =
      this.state.selectedItemsCount > MAX_GROUP_SIZE ? `Maximum group size limit (${MAX_GROUP_SIZE}) reached` : null;
    return (
      <Dialog
        headerProps={{ title: title }}
        mainActionButtonProps={{
          children: `View Selected (${this.state.selectedItemsCount})`,
          isDisabled: this.state.selectedItemsCount < 1 || this.state.selectedItemsCount > MAX_GROUP_SIZE,
          onPress: this.handleSubmitData,
        }}
        closeFn={this.handleCancel}
        footerMessage={{ message: warning, intent: CalloutIntent.Warning }}
      >
        <div
          key="item-1"
          className="form-group venn-diagram-dialog"
          style={{ padding: "10px", marginBottom: "-10px" }}
          onKeyDownCapture={handleTabInForm}
        >
          {!this.state.allItems ? (
            <div style={{ padding: 20 }}>
              <LoadingSpinner inCenter />
            </div>
          ) : (
            content
          )}
        </div>
      </Dialog>
    );
  }

  selectionChanged = (
    selectedItemsCount: number,
    leftSectionSelected: boolean,
    middleSectionSelected: boolean,
    rightSectionSelected: boolean,
  ) => {
    this.setState({ selectedItemsCount, leftSectionSelected, middleSectionSelected, rightSectionSelected });
  };

  // TODO remove ISelectOptionWithGroup and use IGroup directly
  generateOptions = (source: Partial<Group>[]): ISelectOptionWithGroup[] => {
    return (
      source &&
      source.map(
        (x) =>
          ({
            label: `${x.name} (${x.itemCount}) ${x.project ? x.project.name : ""}`,
            value: x.id,
            group: x,
          }) as ISelectOptionWithGroup,
      )
    );
  };

  formatOptionLabel = (
    data: ISelectOptionWithGroup,
    _labelMeta: FormatOptionLabelMeta<ISelectOptionWithGroup>,
  ): ReactElement => {
    const tooltip = `${data.group.name} (${data.group.itemCount}) ${data.group.project?.name}`;
    return (
      <span title={tooltip}>
        <span className="group-option">{data.group.name} </span>
        <span className="count">({data.group.itemCount}) </span>
        <span className="project-name">{data.group.project?.name}</span>
      </span>
    );
  };

  /***
   *
   * HERE FOR REFERENCE, FOR FUTURE USE CASE FOR CUSTOMIZING THE SELECT COMPONENTS EVEN MORE
   * in the redux form field:
   * components={{
          Option: this.createOptionRenderer,
          SingleValue: this.createValueRenderer
        }}


   createOption = (data: ISelectOptionWithGroup): ReactElement => {
    const tooltip = `${data.group.name} (${data.group.itemCount}) ${data.group.project && data.group.project.name}`;
    return (
      <span title={tooltip}>
        <span className="group-option">{data.group.name} </span>
        <span className="count">({data.group.itemCount}) </span>
        <span className="project-name">{data.group.project && data.group.project.name}</span>
      </span>
    );
  }

   createOptionRenderer = (props: OptionProps<ISelectOptionWithGroup>): ReactElement => {
    const data: ISelectOptionWithGroup = props.data;
    const optionLabel = this.createOption(data);
    const className = clsx(`${props.className} venn-select-option`, {
      "is-selected": props.isSelected,  // for retro compatibility, exists with react-select__option--is-selected
      "is-focused": props.isFocused,    // for retro compatibility, exists with react-select__option--is-focused
    });
    return (
      <components.Option {...props} className={className}>
        {optionLabel}
      </components.Option>
    );
  }

   createValueRenderer = (props: OptionProps<ISelectOptionWithGroup>) => {
    const data: ISelectOptionWithGroup = props.data;
    const optionLabel = this.createOption(data);
    return (
      <components.SingleValue {...props} className={`${props.className} venn-select-option `}>
        <div className={"standard-text-ellipsis"}>
          {optionLabel}
        </div>
      </components.SingleValue>
    );
  };
   */
}

export default connect<IVennDiagramDialogAppStateProps, IVennDiagramDialogDispatchProps, IVennDiagramDialogOwnProps>(
  mapStateToProps,
  mapDispatchToProps,

  null,
  {
    forwardRef: true,
  },
)(
  reduxForm({
    form: VennDiagramDialog.FORM_NAME,
  })(VennDiagramDialog) as any,
);

export { VennDiagramDialog };
